import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { ArrowRight, Loader2, AlertCircle } from 'lucide-react';
import ProductCard from '../../components/ProductCard/ProductCard';
import { fetchProducts, fetchCategories } from '../../utils/api';

const COL_CLASS = {
  2: 'grid-cols-1 sm:grid-cols-2',
  3: 'grid-cols-1 sm:grid-cols-2 lg:grid-cols-3',
  4: 'grid-cols-1 sm:grid-cols-2 lg:grid-cols-4',
};

const PAGE_SIZE = 8;

export default function ProductGrid({
  title       = 'All Products',
  columns     = 4,
  showFilters = true,
  orderby     = 'menu_order',
}) {
  const [activeCat, setActiveCat] = useState('all');
  const [page,      setPage]      = useState(1);

  // Category filter pills from WC
  const { data: cats = [] } = useQuery({
    queryKey: ['grid-categories'],
    queryFn:  () => fetchCategories({ per_page: 20, hide_empty: true }),
    staleTime: 5 * 60_000,
  });

  // Products for current page + filter
  const { data: products = [], isLoading, isError } = useQuery({
    queryKey: ['grid-products', activeCat, orderby, page],
    queryFn:  () => {
      const params = { per_page: PAGE_SIZE * page, orderby };
      if (activeCat !== 'all') params.category = activeCat;
      return fetchProducts(params);
    },
    staleTime: 2 * 60_000,
  });

  const gridClass  = COL_CLASS[columns] ?? COL_CLASS[4];
  const shopUrl    = `${window.themeData?.siteUrl ?? ''}/shop/`;
  const hasMore    = products.length >= PAGE_SIZE * page;

  function handleCatChange(catId) {
    setActiveCat(catId);
    setPage(1);
  }

  return (
    <section className="py-20 bg-gradient-to-b from-white to-primary/5">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">

        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-end justify-between mb-10 gap-4">
          <div>
            <h2 className="text-4xl md:text-5xl font-black text-textDark mb-2">{title}</h2>
            <p className="text-textLight text-lg">
              {isLoading ? 'Loading…' : `${products.length} products found`}
            </p>
          </div>
          <a href={shopUrl} className="hidden md:flex items-center gap-1 text-primary font-bold hover:text-primaryHover transition-colors">
            View All <ArrowRight className="w-4 h-4" />
          </a>
        </div>

        {/* Category filter tabs */}
        {showFilters && (
          <div className="flex gap-3 overflow-x-auto pb-2 mb-10 scrollbar-hide -mx-4 px-4">
            <button
              onClick={() => handleCatChange('all')}
              className={`flex-none px-6 py-3 rounded-full font-bold text-sm transition-all whitespace-nowrap ${
                activeCat === 'all'
                  ? 'bg-primary text-white shadow-lg shadow-primary/30'
                  : 'bg-gray-100 text-textLight hover:bg-primary/10 hover:text-primary'
              }`}
            >
              All
            </button>
            {cats.map((cat) => (
              <button
                key={cat.id}
                onClick={() => handleCatChange(String(cat.id))}
                className={`flex-none px-6 py-3 rounded-full font-bold text-sm transition-all whitespace-nowrap ${
                  activeCat === String(cat.id)
                    ? 'bg-primary text-white shadow-lg shadow-primary/30'
                    : 'bg-gray-100 text-textLight hover:bg-primary/10 hover:text-primary'
                }`}
              >
                {cat.name}
                <span className="ml-1.5 opacity-60 font-normal text-xs">({cat.count})</span>
              </button>
            ))}
          </div>
        )}

        {/* States */}
        {isLoading && (
          <div className="flex items-center justify-center py-20 gap-3 text-primary">
            <Loader2 className="w-6 h-6 animate-spin" />
            <span className="font-semibold text-textLight">Loading products…</span>
          </div>
        )}

        {isError && (
          <div className="flex items-center justify-center py-16 gap-3 text-red-400">
            <AlertCircle className="w-5 h-5" />
            <span className="font-semibold">Could not load products. Check WooCommerce is active.</span>
          </div>
        )}

        {!isLoading && !isError && products.length === 0 && (
          <div className="text-center py-16 text-textLight">
            <p className="text-4xl mb-4">🛒</p>
            <p className="font-semibold">No products found in this category.</p>
          </div>
        )}

        {/* Grid */}
        {!isLoading && !isError && products.length > 0 && (
          <div className={`grid ${gridClass} gap-8`}>
            {products.map((product, idx) => (
              <ProductCard key={product.id} product={product} index={idx} />
            ))}
          </div>
        )}

        {/* Load more */}
        {!isLoading && hasMore && (
          <div className="text-center mt-12">
            <button
              onClick={() => setPage((p) => p + 1)}
              className="px-12 py-4 rounded-full border-2 border-primary text-primary font-bold hover:bg-primary hover:text-white transition-all transform hover:-translate-y-1"
            >
              Load More Products
            </button>
          </div>
        )}
      </div>
    </section>
  );
}
